import { Component, Input, OnDestroy, OnInit } from "@angular/core";
import { Store } from "@ngxs/store";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { AdvancedReport, DEFAULT_TYPOGRAPHY, FONT_FAMILY_OPTIONS, GRID_MARGIN_MAX, GRID_VISIBILITY_DEFAULT, GridDensity, PageOrientation, ReportOptionConfig, resolveGridDensity, resolveGridVisibility, resolveReportMargin, TypographySettings } from "@core/data/types/advanced-report";
import { DisplayGrid } from "angular-gridster2";
import { Observable, Subject } from "rxjs";
import { Dialog, Snackbar } from "@core/material";
import { debounceTime, distinctUntilChanged, map, startWith, takeUntil } from "rxjs/operators";
import { ChangeReportGridDensity, EditReportOptions } from "../report-state/advanced-report.state-actions";

@Component({
    selector: 'pv-report-settings',
    templateUrl: 'report-settings.component.html',
    styleUrls: ['report-settings.component.scss'],
    host: {
        class: 'pv-report-settings'
    }
})
export class PvReportSettings implements OnInit, OnDestroy {

    @Input()
    model: AdvancedReport;

    orientationControl: FormControl = new FormControl(null, [Validators.required]);
    fontFamilyControl:  FormControl = new FormControl(null);
    fontFilterControl:  FormControl = new FormControl('');

    // Kept outside formGroup so a margin edit can't be blocked by the form's required-validation,
    // and so it saves on its own without the orientation round-trip. Visual guide only, no rescale.
    marginControl: FormControl = new FormControl(0);

    // Kept outside formGroup: saves on its own like margin, without the orientation round-trip.
    visibilityControl: FormControl = new FormControl(GRID_VISIBILITY_DEFAULT);

    // Kept outside formGroup: density changes confirm + rescale rather than auto-saving.
    densityControl: FormControl = new FormControl(null);

    readonly DisplayGrid = DisplayGrid;

    formGroup: FormGroup = new FormGroup({
        pageOrientation: this.orientationControl,
        fontFamily:      this.fontFamilyControl,
    });

    filteredFonts$: Observable<string[]>;

    private _destroy$ = new Subject();

    constructor(
        private _store: Store,
        private _snackbar: Snackbar,
        private _dialog: Dialog,
    ) {}

    ngOnInit() {
        const { config } = this.model.options;
        const typography = config.inherited?.typography ?? {};

        this.formGroup.reset({
            pageOrientation: config.pageOrientation,
            fontFamily:      typography.fontFamily ?? DEFAULT_TYPOGRAPHY.fontFamily,
        }, { emitEvent: false });

        this.filteredFonts$ = this.fontFilterControl.valueChanges.pipe(
            startWith(''),
            map(term => this._filterFonts(term)),
        );
        this.marginControl.setValue(resolveReportMargin(this.model.options.config), { emitEvent: false });
        this.densityControl.setValue(resolveGridDensity(this.model.options.config), { emitEvent: false });
        this.visibilityControl.setValue(resolveGridVisibility(this.model.options.config), { emitEvent: false });

        this.formGroup.valueChanges
            .pipe(
                takeUntil(this._destroy$),
                distinctUntilChanged(),
                debounceTime(300)
            ).subscribe(() => {
                this.attempt();
            });

        this.marginControl.valueChanges
            .pipe(
                takeUntil(this._destroy$),
                debounceTime(300)
            ).subscribe((value: number) => this.attemptMarginChange(value));

        this.densityControl.valueChanges
            .pipe(takeUntil(this._destroy$))
            .subscribe((value: GridDensity) => this.attemptDensityChange(value));

        this.visibilityControl.valueChanges
            .pipe(takeUntil(this._destroy$))
            .subscribe((value: DisplayGrid) => this.attemptVisibilityChange(value));
    }

    attemptVisibilityChange(value: DisplayGrid) {
        if (value === resolveGridVisibility(this.model.options.config)) return;

        const options: ReportOptionConfig = {
            ...this.model.options.config,
            gridVisibility: value,
        };

        this._store.dispatch(new EditReportOptions(this.model.options.key, this.model.key, options));
    }

    attemptMarginChange(value: number) {
        const clamped = Math.max(0, Math.min(GRID_MARGIN_MAX, Math.floor(Number(value) || 0)));

        // Normalise the field if the user typed something out of range.
        if (clamped !== value) this.marginControl.setValue(clamped, { emitEvent: false });

        if (clamped === resolveReportMargin(this.model.options.config)) return;

        const options: ReportOptionConfig = {
            ...this.model.options.config,
            margin: clamped,
        };

        this._store.dispatch(new EditReportOptions(this.model.options.key, this.model.key, options));
    }

    attemptDensityChange(value: GridDensity) {
        const current: GridDensity = resolveGridDensity(this.model.options.config);
        if (value === current) return;

        const goingDense = value > current;

        const title = goingDense ? 'Switch to a denser grid?' : 'Switch back to the Standard grid?';
        const message = goingDense
            ? 'Your current layout is preserved — every component keeps its size and position, and you can then place them on a finer grid.'
            : 'Every component is resized back to the coarser grid. Components that don\'t line up exactly are rounded down to the nearest cell, so a few may shift slightly.';

        this._dialog.confirm(
            title,
            message,
            goingDense ? 'Switch to Dense' : 'Switch to Standard',
            'Cancel',
            false,
            'reporting-theme'
        )
        .afterClosed()
        .pipe(takeUntil(this._destroy$))
        .subscribe((confirmed: boolean) => {
            // A successful change reloads the report and re-seeds this control; reset here so a
            // cancel or a blocked downscale leaves the control matching the unchanged report.
            this.densityControl.setValue(current, { emitEvent: false });

            if (!confirmed) return;

            this._store.dispatch(new ChangeReportGridDensity(this.model.options.key, this.model.key, value));
        });
    }

    attempt() {
        this.formGroup.markAsDirty();
        this.formGroup.markAllAsTouched();

        if (!this.formGroup.valid) return this._snackbar.error('Invalid data, check your inputs and try again.');

        const form = this.formGroup.value;
        const { config } = this.model.options;

        const typography: TypographySettings = {
            fontFamily: form.fontFamily,
        };

        const options: ReportOptionConfig = {
            ...config,
            pageOrientation: form.pageOrientation as PageOrientation,
            inherited: {
                ...config.inherited,
                typography: typography,
            },
        };

        this._store.dispatch(new EditReportOptions(this.model.options.key, this.model.key, options));
    }

    private _filterFonts(term: string): string[] {
        const search = (term || '').toLowerCase().trim();
        if (!search) return FONT_FAMILY_OPTIONS;
        return FONT_FAMILY_OPTIONS.filter(font => font.toLowerCase().includes(search));
    }

    ngOnDestroy() {
        this._destroy$.next();
        this._destroy$.complete();
    }
}
